import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  AppCard,
  AppCheckbox,
  AppInput,
  AppNoInfo,
  AppRemixIcon,
} from '@/layout/AppComponents';
import { Tab, Tabs } from 'react-bootstrap';
import ReactDOMServer from 'react-dom/server';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../../routing/helpers/router';
import ResourceManagerSidebar from '../../../../layout/screenSpecifics/Modules/ResourceManager/Sidebar/ResourceManagerSidebar';
import colors from '../../../../constants/colors';
import Translator from '../../../../layout/components/helpers/Translator';
import SpinnerLoader from '../../../../layout/components/helpers/SpinnerLoader';
import ObligationCard from '../../../../layout/screenSpecifics/Modules/ResourceManager/Settings/Resource/ObligationCard';
import _ from 'lodash';

export default function ModuleResourceManagerSettingsResourceTemplateCloneScreen() {
  const { moduleBridgeHex, templateHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('obligations');
  const [template, setTemplate] = useState();
  const [initialValues, setInitialValues] = useState({
    name: '',
    description: {},
    files: [],
  });
  const [validationSchema] = useState(
    Yup.object().shape({
      name: Yup.string().required(
        ReactDOMServer.renderToString(<Translator file="client" name="client.please-enter-name" />)
      ),
    })
  );

  // > LOAD TEMPLATE
  const loadTemplate = useCallback(
    async () =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_settings_resourceTemplate_get'
          ),
          {
            moduleBridgeHex,
            templateHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setTemplate(data);

            const updatedInitialValues = {
              ...initialValues,
            };

            updatedInitialValues.features = data?.features?.map((f) => f.hex);
            updatedInitialValues.selectedObligationIDs =
              data?.selectedObligationIDs;

            setInitialValues(updatedInitialValues);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setIsLoading(false)),
    [moduleBridgeHex, templateHex]
  );

  useEffect(() => {
    loadTemplate();
  }, []);
  // < LOAD TEMPLATE

  // > CLONE
  const handleClone = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_settings_resourceTemplate_clone'
          ),
          {
            moduleBridgeHex,
            templateHex,
            payload,
          }
        )
        .then(() => {
          navigate(
            path({
              name: 'user_module_resourceManager_settings_resourceTemplate_list',
              params: {
                moduleBridgeHex,
              },
            })
          );
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, templateHex]
  );
  // < CLONE

  const handleUnSelectAllObligations = useCallback(
    (obligations, selectedObligationIDs, setFieldValue) =>
      setFieldValue(
        'selectedObligationIDs',
        _.difference(selectedObligationIDs, _.map(obligations, 'id'))
      ),
    []
  );

  const handleSelectAllObligations = useCallback(
    (obligations, selectedObligationIDs, setFieldValue) =>
      setFieldValue(
        'selectedObligationIDs',
        _.union(selectedObligationIDs, _.map(obligations, 'id'))
      ),
    []
  );

  if (isLoading) {
    return (
      <AppNoInfo color={colors.primaryLight}>
        <SpinnerLoader size={30} />
      </AppNoInfo>
    );
  }

  return (
    <>
      <ResourceManagerSidebar activeRoute="user_module_resourceManager_settings" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${moduleData?.moduleName}`,
                  route: {
                    name: 'user_module_resourceManager_resource_home',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.settings"
                    />
                  ),
                  route: {
                    name: 'user_module_resourceManager_settings_resourceTemplate_list',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-resourceManager"
                      name="resource.clone.template"
                    />
                  ),
                },
              ]
            : [
                {
                  name: '...',
                },
              ]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <AppCard
            header={`${ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.clone.template"
              />
            )} ${template?.name}`}
          >
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              validateOnChange={false}
              validateOnBlur={false}
              onSubmit={handleClone}
            >
              {({
                setFieldValue,
                handleSubmit,
                values,
                errors,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className="row mb-5">
                    <div className="col-xs-12 col-lg-6">
                      <AppInput
                        title={
                          <Translator
                            file="module-resourceManager"
                            name="resource.name"
                          />
                        }
                        onChange={(value) => setFieldValue('name', value)}
                        value={values.name}
                        error={errors.name}
                        isClearable
                      />
                    </div>
                    <div className="col-xs-12 col-lg-6">
                      {template?.features?.map((f) => (
                        <AppCheckbox
                          key={f.hex}
                          value={values.features?.includes(f.hex)}
                          onChange={(v) => {
                            setFieldValue(
                              'features',
                              v === true
                                ? [...values.features, f.hex]
                                : values.features.filter((fe) => f.hex !== fe)
                            );
                          }}
                          hasBorder
                        >
                          {f.name}
                        </AppCheckbox>
                      ))}
                    </div>
                  </div>

                  <Tabs
                    defaultActiveKey={activeTab}
                    onSelect={(v) => setActiveTab(v)}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Tab
                      eventKey="obligations"
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.obligations"
                        />
                      }
                      className="p-3 border border-top-0 bg-white"
                    >
                      <div>
                        <span
                          className="btn btn-xs btn-outline-light mr-2 mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.select.all"
                            />
                          )}
                          onClick={() =>
                            handleSelectAllObligations(
                              template?.legalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple" />
                        </span>
                        <span
                          className="btn btn-xs btn-outline-light mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.deselect.all"
                            />
                          )}
                          onClick={() =>
                            handleUnSelectAllObligations(
                              template?.legalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple-blank" />
                        </span>
                      </div>
                      {template?.legalObligations?.length > 0 ? (
                        <ul className="list-group">
                          {template?.legalObligations?.map((o) => (
                            <ObligationCard
                              key={o?.id}
                              obligation={o}
                              selectedObligationIDs={
                                values?.selectedObligationIDs
                              }
                              setFieldValue={setFieldValue}
                            />
                          ))}
                        </ul>
                      ) : (
                        <div className="alert alert-info">
                          <Translator
                            file="module-resourceManager"
                            name="resource.no.legal.obligations.assigned"
                          />
                        </div>
                      )}
                    </Tab>
                    <Tab
                      eventKey="internalObligations"
                      title={
                        <Translator
                          file="module-resourceManager"
                          name="resource.internal.obligations"
                        />
                      }
                      className="p-3 border border-top-0 bg-white"
                    >
                      <div>
                        <span
                          className="btn btn-xs btn-outline-light mr-2 mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.select.all"
                            />
                          )}
                          onClick={() =>
                            handleSelectAllObligations(
                              template?.internalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple" />
                        </span>
                        <span
                          className="btn btn-xs btn-outline-light mb-2"
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-resourceManager"
                              name="resource.deselect.all"
                            />
                          )}
                          onClick={() =>
                            handleUnSelectAllObligations(
                              template?.internalObligations,
                              values?.selectedObligationIDs,
                              setFieldValue
                            )
                          }
                        >
                          <AppRemixIcon icon="checkbox-multiple-blank" />
                        </span>
                      </div>
                      {template?.internalObligations?.length > 0 ? (
                        <ul className="list-group">
                          {template?.internalObligations?.map((o) => (
                            <ObligationCard
                              key={o?.id}
                              obligation={o}
                              selectedObligationIDs={
                                values?.selectedObligationIDs
                              }
                              setFieldValue={setFieldValue}
                            />
                          ))}
                        </ul>
                      ) : (
                        <div className="alert alert-info">
                          <Translator
                            file="module-resourceManager"
                            name="no.internal.obligations.available"
                          />
                        </div>
                      )}
                    </Tab>
                  </Tabs>
                  <div className="mt-3 float-right">
                    <button
                      type="submit"
                      className="btn btn-xs btn-success"
                      disabled={isSubmitting}
                    >
                      <Translator
                        file="module-resourceManager"
                        name="resource.create"
                      />
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </AppCard>
        </div>
      </main>
    </>
  );
}
